/* eslint-disable jsx-a11y/anchor-has-content */
import { useApolloClient } from "@apollo/client";
import { getDataOrNull } from "@msys/common";
import { DataGrid, ListHeader, useFormatting } from "@msys/ui";
import { useTranslate } from "@tolgee/react";
import React from "react";
import { useNavigate } from "react-router-dom";
import { RestrictedByProjectPermissionWithDebug } from "../../auth/RestrictedByProjectPermission.js";
import { EntityNumber } from "../../commons/EntityNumber.js";
import {
  BreadcrumbItem,
  Page,
  PageTopbarItem,
} from "../../commons/layout/Page.js";
import { PageContainer } from "../../commons/layout/PageContainer.js";
import { PageHeader } from "../../commons/layout/PageHeader.js";
import { AddTimeReportButton } from "../../features/time-reports/buttons/AddTimeReportButton.js";
import { TimeReportStatusBadge } from "../../features/time-reports/components/TimeReportStatusBadge.js";
import { TimeReportTitle } from "../../features/time-reports/components/TimeReportTitle.js";
import { useDataGridStateStore } from "../../features/users/useDataGridStateStore.js";
import {
  TimeReports__ReportFragment,
  useTimeReportsQuery,
} from "./ProjectTimeReports.generated.js";

interface Props {
  projectId: string;
  prefixBreadcrumbs: BreadcrumbItem[];
  submenuItems: PageTopbarItem[];
  activeSubmenuItem: PageTopbarItem | undefined;
}

export const ProjectTimeReports = ({
  projectId,
  submenuItems,
  activeSubmenuItem,
  prefixBreadcrumbs,
}: Props) => {
  const navigate = useNavigate();

  const { t } = useTranslate(["TimeReports", "Global"]);
  const { getFormattedDate } = useFormatting();

  const breadcrumbs = React.useMemo(
    () => [
      ...prefixBreadcrumbs,
      {
        title: t("Tasks", {
          ns: "TimeReports",
        }),
        to: `/projects/${projectId}/tasks`,
      },
      {
        title: t("Time Reports", {
          ns: "TimeReports",
        }),
        to: `/projects/${projectId}/tasks/reports`,
      },
    ],
    [prefixBreadcrumbs, t, projectId]
  );

  const client = useApolloClient();
  const query = useTimeReportsQuery({
    client,
    variables: {
      projectId,
    },
  });

  const project = getDataOrNull(query.data?.project)?.project;
  const timeReports = project?.timeReports ?? [];

  const stateStore = useDataGridStateStore("ProjectTimeReports");

  return (
    <Page
      title={project?.title}
      submenuItems={submenuItems}
      breadcrumbs={breadcrumbs}
      header={
        <PageHeader
          breadcrumbs={breadcrumbs}
          submenuItems={submenuItems}
          activeSubmenuItem={activeSubmenuItem}
        />
      }
    >
      {project && (
        <PageContainer>
          <ListHeader
            title={t("Time Reports", {
              ns: "TimeReports",
            })}
            CreateButton={
              <RestrictedByProjectPermissionWithDebug
                permission="quote_document_update"
                project={project}
              >
                <AddTimeReportButton projectId={projectId} type="button" />
              </RestrictedByProjectPermissionWithDebug>
            }
            sx={{ mb: 2 }}
          />

          <RestrictedByProjectPermissionWithDebug
            permission="quote_document_read"
            project={project}
          >
            <DataGrid
              stateStore={stateStore}
              loading={query.loading}
              hideFooter={true}
              columns={[
                {
                  field: "number",
                  headerName: t("No.", {
                    ns: "Global",
                  }),
                  flex: 1,
                  minWidth: 100,
                  sortable: false,
                  renderCell: ({
                    row: report,
                  }: {
                    row: TimeReports__ReportFragment;
                  }) => <EntityNumber noLabel number={report.number} />,
                },
                {
                  field: "status",
                  headerName: t("Status", {
                    ns: "TimeReports",
                  }),
                  flex: 1,
                  minWidth: 100,
                  sortable: false,
                  renderCell: ({
                    row: report,
                  }: {
                    row: TimeReports__ReportFragment;
                  }) => <TimeReportStatusBadge report={report} />,
                },
                {
                  field: "title",
                  headerName: t("Time report title", {
                    ns: "TimeReports",
                  }),
                  flex: 3,
                  minWidth: 100,
                  sortable: false,
                  renderCell: ({
                    row: report,
                  }: {
                    row: TimeReports__ReportFragment;
                  }) => <TimeReportTitle report={report} />,
                },
                {
                  field: "fromDate",
                  headerName: t("Time period", {
                    ns: "TimeReports",
                  }),
                  flex: 1,
                  minWidth: 100,
                  sortable: false,
                  renderCell: ({
                    row: report,
                  }: {
                    row: TimeReports__ReportFragment;
                  }) =>
                    `${getFormattedDate(report.fromDate)} – ${getFormattedDate(
                      report.tillDate
                    )}`,
                },
              ]}
              rows={timeReports}
              onRowClick={report =>
                navigate(`/projects/${projectId}/tasks/reports/${report.id}`)
              }
            />
          </RestrictedByProjectPermissionWithDebug>
        </PageContainer>
      )}
    </Page>
  );
};
